import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { isAdminFlow } from './url-utils';

/**
 * Service for tracking user location during login
 * Implements multiple fallback strategies for obtaining location data
 */
@Injectable({
  providedIn: 'root'
})
export class LocationService {
  // Create a private property to store the cached request
  private locationTrackingRequest: Observable<any> | null = null;
  constructor(private http: HttpClient) { }

  /**
   * Get user location and send it to the backend
   * @param tokenClaims - The claims from the authentication token
   * @returns Observable with the API response
   */
  trackUserLocation(tokenClaims: any): Observable<any> {
    // First try to get location from Entra ID token if available
    let locationGeographic = this.getLocationFromToken(tokenClaims);
    // If location not available in token, use IP-based geolocation
    if (!locationGeographic) {
      // console.log('No location found in token, trying IP-based geolocation');
      return this.getIPBasedLocation().pipe(
        catchError(() => {
          // console.log('Error getting IP-based location:');
          // If IP geolocation fails, use timezone fallback
          return this.getBrowserGeolocation().pipe(
            catchError(() => {
              // console.log('Error getting fallback location:');
              // If all location methods fail, use a fallback value
              return this.sendLocationToBackend('');
            })
          );
        })
      );
    } else {
      // If we have location from token, send it directly
      return this.sendLocationToBackend(locationGeographic);
    }
  }

  /**
   * Get location using IP-based geolocation with CORS-friendly API
   * @returns Observable with API response after sending location to backend
   */
  private getIPBasedLocation(): Observable<any> {
    // console.log('Getting location from IP address using CORS-friendly API');
    // Use a CORS-friendly geolocation API
    // Alternative APIs if this one doesn't work:
    // - https://geolocation-db.com/json/
    // - https://api.ipgeolocation.io/ipgeo (requires API key)
    const apiUrl = 'https://ipapi.co/jsonp/?callback=callback';
    // Use JSONP approach to avoid CORS issues
    return this.http.jsonp(apiUrl, 'callback').pipe(
      switchMap((res: any) => {
        // Extract location data from the response
        const city = res.city || '';
        const country = res.country_name || '';
        const locationString = `${city}, ${country}`;
        // Send location to backend
        return this.sendLocationToBackend(locationString);
      }),
      catchError(() => {
        // console.log('IP geolocation error:', error);
        // Try alternative API if first one fails
        return this.getAlternativeIPLocation();
      })
    );
  }

  /**
   * Alternative IP geolocation method using a different API
   * @returns Observable with API response after sending location to backend
   */
  private getAlternativeIPLocation(): Observable<any> {
    const apiUrl = 'https://geolocation-db.com/json/';
    return this.http.get(apiUrl).pipe(
      switchMap((res: any) => {
        // Extract location data from the response
        const city = res.city || '';
        const country = res.country_name || '';
        const locationString = `${city}, ${country}`;
        // Send location to backend
        return this.sendLocationToBackend(locationString);
      }),
      catchError((error: any) => {
        // console.log('Alternative IP geolocation error:', error);
        throw error;
      })
    );
  }

  /**
   * Extract location information from token claims if available
   * @param tokenClaims - The claims from the authentication token
   * @returns Location string in "City, Country" format or null if not available
   */
  private getLocationFromToken(tokenClaims: any): string | null {
    // Check for location information in various possible claim fields
    if (tokenClaims) {
      // Check for standard location claims
      const city = tokenClaims.city ||
        tokenClaims.ctry ||
        tokenClaims.state ||
        tokenClaims.locality ||
        '';

      const country = tokenClaims.country ||
        tokenClaims.countryCode ||
        tokenClaims.country_name ||
        '';
      // If we have either city or country, return a location string
      if (city || country) {
        const locationString = `${city}, ${country}`.trim();
        return locationString;
      }

      // Check for any custom location claims that might be present
      for (const key in tokenClaims) {
        if (key.toLowerCase().includes('location') ||
          key.toLowerCase().includes('geo') ||
          key.toLowerCase().includes('place')) {
          return tokenClaims[key];
        }
      }
    }
    // console.log('No location information found in token claims');
    return null;
  }

  /**
   * Get location using browser's Geolocation API (requires user permission)
   * This is an additional method that could be used if other methods fail
   * @returns Observable with location string
   */
  private getBrowserGeolocation(): Observable<string> {
    return new Observable((observer: any) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            // Get coordinates
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            // For simplicity, just use coordinates as location
            // In a production app, you might want to reverse geocode these
            const locationString = `${lat}, ${lng}`;
            observer.next(locationString);
            observer.complete();
          },
          error => {
            observer.error(error);
          },
          { timeout: 10000, enableHighAccuracy: false }
        );
      } else {
        observer.error('Geolocation not supported');
      }
    });
  }

  /**
   * Detect the user's platform (Web, iOS, Android)
   * @returns The platform identifier string
   */
  detectPlatform(): string {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    // Check for iOS devices
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      return 'iOS';
    }
    
    // Check for Android devices
    if (/android/i.test(userAgent)) {
      return 'Android';
    }
    
    // Default to Web for all other platforms
    return 'Web';
  }

  /**
   * Send location data to backend API
   * @param locationGeographic - Location string in "City, Country" format
   * @returns Observable with API response
   */
  private sendLocationToBackend(locationGeographic: string): Observable<any> {
    // If we already have an active request, return it
    if (this.locationTrackingRequest) {
      return this.locationTrackingRequest;
    }
    // Format should be "City Country" without comma for the endpoint
    // Replace comma with space as required by the backend
    const formattedLocation = locationGeographic.replace(/,/g, ' ').trim();
    // Ensure there are no double spaces
    const cleanedLocation = formattedLocation.replace(/\s+/g, ' ');
    // Detect the platform
    const platform = this.detectPlatform();
    // Build the URL with both location and platform parameters
    const staffUrl = `${environment.apiConfig.uri}/admin/Candidates/AddStaffPortalLogin?locationGeographic=${encodeURIComponent(cleanedLocation)}`;
    const canUrl = `${environment.apiConfig.uri}/Candidate/AddCandidatePortalLogin?locationGeographic=${encodeURIComponent(cleanedLocation)}&platform=${encodeURIComponent(platform)}`;
    // Use the isAdminFlow utility function 
    const url = isAdminFlow() ? staffUrl : canUrl;
    // Create and cache the request
    this.locationTrackingRequest = this.http.post(url, {}).pipe(
      map((response: any) => {
        return response;
      }),
      // Still handle errors but make sure we're not causing multiple calls
      catchError((error:any) => {
        // Return a success object anyway to prevent blocking the UI
        return of({ success: false, error: error.message });
      }),
      // Add shareReplay to ensure the request is only made once
      shareReplay(1)
    );
    
    return this.locationTrackingRequest;
  }
}